// EmptyTile — shared Empty-State-Component (Patch 4, Sprint 2 Tag 10).
//
// Anti-Pattern den wir hier vermeiden: "0,00 €" rendern wenn Banking
// nicht angebunden ist → das ist eine Lüge, frisst Trust. Stattdessen
// ehrlicher Empty-State mit konkretem Lösungs-Hinweis + CTA.
//
// Props bewusst klein gehalten: icon (ReactNode, idR. inline-SVG oder
// lucide-react sobald installiert), headline (1 Satz), subtext (1 Satz)
// und optionaler CTA-Link.
//
// Visuelles: dashed border + dezente neutral-Farben — soll auffallen,
// aber nicht schreien. Tiles bleiben in Grid-Position erhalten.

import { ReactNode } from "react";
import { Link } from "react-router-dom";

export interface EmptyTileProps {
  icon: ReactNode;
  headline: string;
  subtext?: string;
  cta_label?: string;
  cta_href?: string;
  /** Grid-Spans (analog HeroTile). */
  spanWide?: boolean;
  /** Optional ARIA-Label für Screenreader. */
  ariaLabel?: string;
}

export default function EmptyTile({
  icon,
  headline,
  subtext,
  cta_label,
  cta_href,
  spanWide,
  ariaLabel,
}: EmptyTileProps) {
  return (
    <section
      aria-label={ariaLabel ?? headline}
      data-testid="empty-tile"
      className={`
        relative rounded-2xl border-2 border-dashed border-slate-700/60
        bg-slate-950/40 p-4 sm:p-5
        flex flex-col items-start gap-2
        ${spanWide ? "md:col-span-2" : ""}
      `}
    >
      <div className="text-slate-500 [&_svg]:h-5 [&_svg]:w-5">{icon}</div>
      <h3 className="text-sm font-semibold text-slate-200">{headline}</h3>
      {subtext && <p className="text-xs text-slate-400 leading-relaxed">{subtext}</p>}
      {cta_label && cta_href && (
        <Link
          to={cta_href}
          className="mt-1 inline-flex items-center min-h-11 min-w-11 px-3 -ml-3 text-xs font-medium text-sky-300 hover:text-sky-200 underline underline-offset-2"
        >
          → {cta_label}
        </Link>
      )}
    </section>
  );
}

// ---- Inline-Icon-Set (lucide-react Drop-In sobald installiert) ----

export const IconDatabase = (
  <svg fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24" aria-hidden>
    <ellipse cx="12" cy="5" rx="8" ry="3" />
    <path d="M4 5v6c0 1.7 3.6 3 8 3s8-1.3 8-3V5" />
    <path d="M4 11v6c0 1.7 3.6 3 8 3s8-1.3 8-3v-6" />
  </svg>
);

export const IconAlertCircle = (
  <svg fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24" aria-hidden>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 8v5" />
    <path d="M12 16h.01" />
  </svg>
);

export const IconTrendingUp = (
  <svg fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24" aria-hidden>
    <path d="M3 17l6-6 4 4 7-7" />
    <path d="M14 8h6v6" />
  </svg>
);

export const IconClock = (
  <svg fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24" aria-hidden>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </svg>
);

export const IconCheckCircle = (
  <svg fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24" aria-hidden>
    <circle cx="12" cy="12" r="9" />
    <path d="M8 12l3 3 5-6" />
  </svg>
);
